import React from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { Tabs, TabsContent } from './ui/tabs';
import { ChaptersTimeline } from './ChaptersTimeline';
import { ChaptersListView, type ChapterSceneDragEndResult } from './ChaptersListView';
import { SortableChapterCard, SortableChapterRow, Chapter } from './Chapters';
import type { Scene } from './Scenes';
import type { MentionableEntity } from './RichTextField';
import type { ViewSwitcherMode } from './ViewSwitcher';
import { cn } from '@/lib/utils';
import type { AvailableScenesTree } from '../utils/available-scenes-tree';

type ChaptersViewMode = Extract<ViewSwitcherMode, 'list' | 'grid' | 'table' | 'timeline'>;

export const CHAPTER_VIEW_TAB_CONTENT_CLASSNAME = 'mt-0 min-h-0 flex-1 flex flex-col overflow-hidden';
export const CHAPTER_VIEW_SCROLLABLE_TAB_CONTENT_CLASSNAME = 'mt-0 min-h-0 flex-1 overflow-y-auto p-8';
export const CHAPTER_LIST_CONTENT_PADDING_CLASSNAME = 'p-8';
export const CHAPTER_SIDEBAR_LIST_CONTENT_PADDING_CLASSNAME = 'p-4';

interface ChaptersOutlineProps {
  chapters: Chapter[];
  viewMode: ChaptersViewMode;
  onViewModeChange: (mode: ChaptersViewMode) => void;
  projectId?: string;
  isSidebar?: boolean;
  onDragEnd: (event: DragEndEvent) => void;
  sensors: any;
  onEdit: (chapter: Chapter) => void;
  onDelete: (chapter: Chapter) => void;
  onAddChapter?: () => void;
  scenesByChapter?: Map<string, Scene[]>;
  mentionableEntities?: MentionableEntity[];
  onAddScene?: (chapter: Chapter) => void;
  onTitleChange?: (chapter: Chapter, title: string) => void;
  onSummaryChange?: (chapter: Chapter, summary: string) => void;
  onEditScene?: (scene: Scene, chapter?: Chapter) => void;
  onSceneDragEnd?: (result: ChapterSceneDragEndResult) => void | Promise<void>;
  unlinkedScenes?: Scene[];
  availableScenesTree?: AvailableScenesTree;
  deletingChapterId: string | null;
  editingChapterId: string | null;
  token: string | null;
  masterKey: string | null;
  documentId: string | undefined;
  t: (key: string) => string;
  chapterTimelineGroupBy?: 'none' | 'character' | 'storyline' | 'scene';
  onChapterTimelineGroupByChange?: (mode: 'none' | 'character' | 'storyline' | 'scene') => void;
  chapterTimelineGroupByCharacters?: Set<string>;
  onChapterTimelineGroupByCharactersChange?: (chars: Set<string>) => void;
  chapterTimelineGroupByStorylines?: Set<string>;
  onChapterTimelineGroupByStorylinesChange?: (storylines: Set<string>) => void;
  chapterTimelineGroupByScenes?: Set<string>;
  onChapterTimelineGroupByScenesChange?: (scenes: Set<string>) => void;
  entityRefs?: Array<{ sourceType: string; sourceId: string; targetType: string; targetId: string }>;
  characters?: Array<{ id: string; name: string; documentId?: string }>;
  storylines?: Array<{ id: string; title: string }>;
  scenes?: Array<{ id: string; title: string }>;
}

export const ChaptersOutline: React.FC<ChaptersOutlineProps> = ({
  chapters,
  viewMode,
  onViewModeChange,
  isSidebar = false,
  onDragEnd,
  sensors,
  onEdit,
  onDelete,
  onAddChapter,
  scenesByChapter = new Map(),
  mentionableEntities = [],
  onAddScene,
  onTitleChange,
  onSummaryChange,
  onEditScene,
  onSceneDragEnd,
  unlinkedScenes = [],
  availableScenesTree,
  deletingChapterId,
  editingChapterId,
  token,
  masterKey,
  documentId,
  t,
  chapterTimelineGroupBy = 'none',
  onChapterTimelineGroupByChange,
  chapterTimelineGroupByCharacters = new Set(),
  onChapterTimelineGroupByCharactersChange,
  chapterTimelineGroupByStorylines = new Set(),
  onChapterTimelineGroupByStorylinesChange,
  chapterTimelineGroupByScenes = new Set(),
  onChapterTimelineGroupByScenesChange,
  entityRefs = [],
  characters = [],
  storylines = [],
  scenes = [],
}) => {
  if (chapters.length === 0) {
    return null;
  }

  const sortedChapters = [...chapters].sort((a, b) => (a.order || 0) - (b.order || 0));
  const sortableIds = sortedChapters.map((ch, index) => ch.id || `chapter-${index}`);
  const listContentPaddingClassName = isSidebar
    ? CHAPTER_SIDEBAR_LIST_CONTENT_PADDING_CLASSNAME
    : CHAPTER_LIST_CONTENT_PADDING_CLASSNAME;
  const scrollableTabContentClassName = cn(
    CHAPTER_VIEW_SCROLLABLE_TAB_CONTENT_CLASSNAME,
    isSidebar && 'p-4',
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Tabs
        value={viewMode}
        onValueChange={(value) => onViewModeChange(value as ChaptersViewMode)}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <TabsContent value="list" className={CHAPTER_VIEW_TAB_CONTENT_CLASSNAME}>
          <ChaptersListView
            chapters={sortedChapters}
            scenesByChapter={scenesByChapter}
            unlinkedScenes={unlinkedScenes}
            availableScenesTree={availableScenesTree}
            mentionableEntities={mentionableEntities}
            listContentClassName={listContentPaddingClassName}
            currentDocumentId={documentId}
            onEditChapter={onEdit}
            onTitleChange={onTitleChange ?? (() => {})}
            onSummaryChange={onSummaryChange ?? (() => {})}
            onAddScene={onAddScene ?? (() => {})}
            onEditScene={onEditScene}
            onSceneDragEnd={onSceneDragEnd}
            onAddChapter={onAddChapter}
            t={t}
          />
        </TabsContent>
        <TabsContent value="grid" className={scrollableTabContentClassName}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedChapters.map((chapter, index) => (
                    <SortableChapterCard
                      key={chapter.id || index}
                      chapter={chapter}
                      index={index}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      deletingChapterId={deletingChapterId}
                      editingChapterId={editingChapterId}
                      token={token}
                      masterKey={masterKey}
                      documentId={documentId}
                      t={t}
                    />
                  ))}
                {onAddChapter && (
                  <button
                    type="button"
                    onClick={onAddChapter}
                    className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow flex flex-col items-center justify-center min-h-[140px] gap-2 text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-8 w-8" />
                    <span className="text-lg font-medium">{t('add')}</span>
                  </button>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </TabsContent>
        <TabsContent value="timeline" className={scrollableTabContentClassName}>
          <ChaptersTimeline
            chapters={chapters}
            chapterTimelineGroupBy={chapterTimelineGroupBy}
            onChapterTimelineGroupByChange={onChapterTimelineGroupByChange ?? (() => {})}
            chapterTimelineGroupByCharacters={chapterTimelineGroupByCharacters}
            onChapterTimelineGroupByCharactersChange={onChapterTimelineGroupByCharactersChange ?? (() => {})}
            chapterTimelineGroupByStorylines={chapterTimelineGroupByStorylines}
            onChapterTimelineGroupByStorylinesChange={onChapterTimelineGroupByStorylinesChange ?? (() => {})}
            chapterTimelineGroupByScenes={chapterTimelineGroupByScenes}
            onChapterTimelineGroupByScenesChange={onChapterTimelineGroupByScenesChange ?? (() => {})}
            entityRefs={entityRefs}
            characters={characters}
            storylines={storylines}
            scenes={scenes}
            onEditChapter={onEdit}
            t={t}
          />
        </TabsContent>
        <TabsContent value="table" className={scrollableTabContentClassName}>
          <div
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    minWidth: '800px',
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
                      <th
                        aria-label={t('chapter_order')}
                        style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          color: 'var(--foreground)',
                          width: '1%',
                          whiteSpace: 'nowrap',
                        }}
                      />
                      <th
                        style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          color: 'var(--foreground)',
                        }}
                      >
                        {t('title')}
                      </th>
                      <th
                        style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          color: 'var(--foreground)',
                        }}
                      >
                        {t('words_goal')}
                      </th>
                      <th
                        style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          color: 'var(--foreground)',
                        }}
                      >
                        {t('summary')}
                      </th>
                      <th
                        style={{
                          padding: '1rem',
                          textAlign: 'center',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          color: 'var(--foreground)',
                        }}
                      >
                        {t('actions')}
                      </th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={sortableIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {sortedChapters.map((chapter, index) => (
                          <SortableChapterRow
                            key={chapter.id || index}
                            chapter={chapter}
                            index={index}
                            isLast={index === sortedChapters.length - 1 && !onAddChapter}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            deletingChapterId={deletingChapterId}
                            editingChapterId={editingChapterId}
                            t={t}
                          />
                        ))}
                      {onAddChapter && (
                        <tr
                          role="button"
                          tabIndex={0}
                          onClick={onAddChapter}
                          onKeyDown={(e) => e.key === 'Enter' && onAddChapter()}
                          style={{
                            borderTop: '1px solid var(--border)',
                            transition: 'background-color 0.2s',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--muted)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                          }}
                        >
                          <td
                            colSpan={5}
                            style={{
                              padding: '1rem',
                              fontSize: '0.875rem',
                              color: '#9ca3af',
                              textAlign: 'center',
                              fontWeight: '500',
                            }}
                          >
                            <span className="inline-flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              {t('add')}
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

